import { useState } from "react";

import { useUploadFileToGoogleStore } from "@/queries/google-file-search-store";
import { displayToast, displayErrorToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface UseFileUploadProps {
  selectedStoreName: string | null;
  provider?: string;
}

interface UploadingFile {
  name: string;
  size: number;
  tempId: string;
}

export const useFileUpload = ({ selectedStoreName, provider = "google" }: UseFileUploadProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadingFile>>(new Map());

  const { mutateAsync: uploadFile, isLoading: isUploadingFile } =
    useUploadFileToGoogleStore(provider);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      event.target.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadAndAttach = async () => {
    if (selectedFiles.length === 0 || !selectedStoreName) {
      displayErrorToast("Please select files and a store");
      return;
    }

    const filesToUpload = [...selectedFiles];

    const fileToTempIdMap = new Map<File, string>();
    filesToUpload.forEach(file => {
      const tempId = `uploading-${Date.now()}-${Math.random()}`;
      fileToTempIdMap.set(file, tempId);
      setUploadingFiles(prev => {
        const next = new Map(prev);
        next.set(tempId, { name: file.name, size: file.size, tempId });
        return next;
      });
    });
    setSelectedFiles([]);

    let successCount = 0;
    let failCount = 0;

    for (const file of filesToUpload) {
      const tempId = fileToTempIdMap.get(file);
      try {
        await uploadFile({ file, storeName: selectedStoreName });
        successCount += 1;
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(`Failed to upload ${file.name}:`, error);
        failCount += 1;
      } finally {
        if (tempId) {
          setUploadingFiles(prev => {
            const next = new Map(prev);
            next.delete(tempId);
            return next;
          });
        }
      }
    }

    if (successCount > 0) {
      displayToast(
        `Successfully uploaded ${successCount} file${successCount > 1 ? "s" : ""}`,
        ToastType.success,
      );
    }
    if (failCount > 0) {
      displayErrorToast(`Failed to upload ${failCount} file${failCount > 1 ? "s" : ""}`);
    }
  };

  return {
    selectedFiles,
    isDragOver,
    uploadingFiles: Array.from(uploadingFiles.values()),
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveFile,
    handleUploadAndAttach,
    isUploading: isUploadingFile,
  };
};
